import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Formik, FormikHelpers } from 'formik'
import { omit, map, prop, equals } from 'ramda'
import { Stack, Heading, useToast, useDialogController, Box } from '@machobear/design-system'
import queryClient from '@/components/query-client'

import DeleteConfirmationModal from './delete-confirmation-modal'
import FormFooter from './footer'
import FormWrapper from '../form-wrapper'
import FeaturesSection from '../form-details/features-section'
import LandlordSection from '../form-details/landlord-section'
import InfoSection from '../form-details/info-section'
import TermsSection from '../form-details/terms-section'
import PhotoSection from '../form-media/photo-section'
import VideoSection from '../form-media/video-section'
import TourSection from '../form-media/tour-section'
import { DetailsFormValues, Listing, ListingLandlords } from '../types'
import { editValidationSchema } from '../util/vallidationSchema'
import { formatLandlordData } from '../util/landlords'
import { formatListingAddress, getParkingStorageType } from '../util'
import { useLandlordValues } from '../hooks/use-landlord-values'
import useRedirectDraftListing from '../hooks/use-redirect-draft-listing'

const EditListingForm = ({
  data,
  landlordData,
  isLoading,
  refs
}: {
  data: Listing
  landlordData: ListingLandlords
  isLoading: boolean
  refs: Record<string, React.RefObject<HTMLDivElement>>
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const showToast = useToast()
  const userId = useSelector((state: any) => state.profile?.id)
  const listingId = data.listing_id

  useRedirectDraftListing(data)

  const { visible, showModal, closeModal } = useDialogController()

  const { primaryLandlord, setPrimaryLandlord, secondaryLandlords, setSecondaryLandlords } =
    useLandlordValues(landlordData)

  const formattedValues = omit(['state_machine'], data)
  const parkingType = getParkingStorageType(formattedValues?.parking?.fee_frequency_txt_id)
  const storageType = getParkingStorageType(formattedValues?.storage?.fee_frequency_txt_id)
  const initialValues: DetailsFormValues = {
    ...(formattedValues as Listing),
    isBoostYourListing: false,
    duration: '',
    parkingType,
    storageType,
    // it was requested that we don't display zero (0) values in these fields
    allow_cats: data.allow_cats || '',
    allow_dogs: data.allow_dogs || ''
  }

  const saveLandlords = async () => {
    const { formattedLandlordData, removedSelfFromListing } = formatLandlordData({
      listingId,
      userId,
      primaryLandlord,
      secondaryLandlords
    })
    const response = await dispatch.listing.updateLandlords(formattedLandlordData, {
      noLoading: true
    })
    return { landlordResponse: response, removedSelfFromListing }
  }

  const handleDelete = async () => {
    const { response } = await dispatch.listing.delete(listingId)
    const address = formatListingAddress(data)
    if (response?.ok) {
      showToast({
        message: t('l.listing.edit.listing_deleted_toast.title', {
          address,
          defaultValue: '{{ address }} has been deleted!'
        }),
        description: t(
          'l.listing.edit.listing_deleted_toast.text',
          'If this was done in error, please contact us at support@liv.rent.'
        ),
        tone: 'positive'
      })
      history.push('/listings')
    } else {
      showToast({ message: t('Unable to delete listing'), tone: 'critical' })
    }
  }

  const saveListing = async (values: DetailsFormValues) => {
    const oldImageOrder = map(prop('id'), data.images)
    const newImageOrder = map(prop('id'), values.images)

    // If the images have been reordered, update on the listing
    if (values.images && values?.images?.length > 0 && !equals(oldImageOrder, newImageOrder)) {
      const { response: sortResponse } = await dispatch.listing.sortImages(values.images)
      if (!sortResponse.ok) return sortResponse
    }

    const { response } = await dispatch.listing.updateUnit(values)

    return response
  }

  const onSubmit = async (
    values: DetailsFormValues,
    { setSubmitting }: FormikHelpers<DetailsFormValues>
  ) => {
    const response = await saveListing(values)
    if (response?.ok) {
      const { landlordResponse, removedSelfFromListing } = await saveLandlords()
      if (landlordResponse?.ok) {
        showToast({
          message: t('l.listing.edit.listing_edited_toast_text', 'Your listing has been saved!'),
          tone: 'positive'
        })
        removedSelfFromListing ? history.push('/listings') : history.push(`/listing/${listingId}`)
      }
      queryClient.invalidateQueries({ queryKey: ['listing-info', listingId] })
    }

    setSubmitting(false)
  }

  return (
    <>
      <DeleteConfirmationModal
        visible={visible}
        closeModal={closeModal}
        handleDelete={handleDelete}
      />
      <Box marginTop={{ mobile: 'large', tablet: 'large', desktop: 'none' }}>
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues}
          validationSchema={editValidationSchema}
          enableReinitialize
        >
          {({ isSubmitting }) => {
            return (
              <FormWrapper>
                <Form>
                  <Stack align="left" space={{ mobile: 'gutter', desktop: 'large' }}>
                    <Box paddingLeft={{ mobile: 'gutter', tablet: 'none' }}>
                      <Heading level="4" component="h2">
                        {t('Edit Listing', 'Edit Listing')}
                      </Heading>
                    </Box>
                    <div ref={refs.information}>
                      <InfoSection showUnitFields isLoading={isLoading} />
                    </div>
                    <div ref={refs.terms}>
                      <TermsSection isLoading={isLoading} />
                    </div>
                    <div ref={refs.features}>
                      <FeaturesSection isLoading={isLoading} />
                    </div>
                    <div ref={refs.photo}>
                      <PhotoSection data={data} isLoading={isLoading} />
                    </div>
                    <div ref={refs.video}>
                      <VideoSection isLoading={isLoading} />
                    </div>
                    <div ref={refs.tour}>
                      <TourSection isLoading={isLoading} />
                    </div>
                    <div ref={refs.landlords}>
                      <LandlordSection
                        isLoading={isLoading}
                        primaryLandlord={primaryLandlord}
                        secondaryLandlords={secondaryLandlords}
                        setPrimaryLandlord={setPrimaryLandlord}
                        setSecondaryLandlords={setSecondaryLandlords}
                      />
                    </div>
                    <FormFooter
                      isLoading={isLoading}
                      onDelete={showModal}
                      isSubmitting={isSubmitting}
                    />
                  </Stack>
                </Form>
              </FormWrapper>
            )
          }}
        </Formik>
      </Box>
    </>
  )
}

export default EditListingForm
